import { ExportJobDto, ExportRequestOptions, ExportType } from '../../types/export';
import { LabelJobDto } from '../../types/label';
import { MappingDocumentJobDto } from '../../types/mappingDocument';

export type JobKind = 'export' | 'label' | 'mapping';
export type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type StatusFilter = 'all' | 'completed' | 'active' | 'failed';

export interface JobItemCounts {
  noun: string;
  total: number;
  succeeded: number;
  failed: number;
}

/** One row on the dashboard, independent of which job API it came from. */
export interface DashboardJob {
  id: string;
  kind: JobKind;
  status: JobStatus;
  partnerName: string;
  userId?: string;
  typeLabel: string;
  /** Secondary line under the type (export mode, FC selection, mapping filters). */
  typeDetail: string | null;
  /** Extra label/value facts for the details drawer. */
  facts: ReadonlyArray<readonly [string, string]>;
  createdAt: number;
  completedAt?: number;
  progress: number;
  items: JobItemCounts | null;
  files: string[];
  error?: string;
}

export const JOB_KINDS: readonly JobKind[] = ['export', 'label', 'mapping'];

const STATUS_FILTER_MATCH: Record<StatusFilter, readonly JobStatus[] | null> = {
  all: null,
  completed: ['completed'],
  active: ['pending', 'processing'],
  failed: ['failed'],
};

// Mirrors the export type labels served by the backend (export.controller.ts).
const EXPORT_TYPE_LABELS: Record<string, string> = {
  [ExportType.FULFILLMENT_CONTEXTS]: 'Fulfillment contexts export',
  [ExportType.TENANTS]: 'Tenants export',
  [ExportType.ORIGINS]: 'Origins export',
  [ExportType.CARRIER_CONTRACTS]: 'Carrier contracts export',
  [ExportType.CARRIER_RATES]: 'Carrier rates export',
};

const MAX_FILTERS_SHOWN = 3;

const toTime = (d: Date | string | undefined): number | undefined => (d ? new Date(d).getTime() : undefined);

// Older jobs predate FC selection and have no exportAllFCs flag.
export const getFcSelectionLabel = (requestOptions?: ExportRequestOptions): string | null => {
  if (requestOptions?.exportAllFCs === true) return 'All FCs';
  const ids = requestOptions?.fulfillmentContextIds;
  return requestOptions?.exportAllFCs === false && Array.isArray(ids) ? `${ids.length} FC${ids.length === 1 ? '' : 's'}` : null;
};

const summarize = (label: string, values: string[]): string =>
  `${label}: ${values.slice(0, MAX_FILTERS_SHOWN).join(', ')}${values.length > MAX_FILTERS_SHOWN ? ` +${values.length - MAX_FILTERS_SHOWN} more` : ''}`;

function fromExportJob(job: ExportJobDto): DashboardJob {
  const mode = job.exportMode ? (job.exportMode === 'consolidated' ? 'Consolidated' : 'Detailed') : null;
  const fcLabel = getFcSelectionLabel(job.requestOptions);
  return {
    id: job.jobId,
    kind: 'export',
    status: job.status,
    partnerName: job.partnerName || 'Unknown',
    userId: job.userId,
    typeLabel: EXPORT_TYPE_LABELS[job.exportType] ?? job.exportType,
    typeDetail: [mode, fcLabel].filter(Boolean).join(' · ') || null,
    facts: [],
    createdAt: toTime(job.createdAt)!,
    completedAt: toTime(job.completedAt),
    progress: job.progress ?? 0,
    items: { noun: 'item', total: job.totalItems, succeeded: job.successfulItems, failed: job.failedItems },
    files: job.files ?? [],
    error: job.error,
  };
}

function fromLabelJob(job: LabelJobDto): DashboardJob {
  const facts: Array<readonly [string, string]> = [];
  if (job.labelFormats?.length) facts.push(['Formats', job.labelFormats.join(', ')]);
  if (job.totalFulfillmentContexts > 0) {
    facts.push(['Scope', `${job.totalFulfillmentContexts} contexts, ${job.totalCarrierServiceMethods} service methods`]);
  }
  return {
    id: job.jobId,
    kind: 'label',
    status: job.status,
    partnerName: job.partnerName || 'Unknown',
    userId: job.userId,
    typeLabel: 'Label print batch',
    typeDetail: job.totalLabels > 0 ? `${job.totalLabels} labels` : null,
    facts,
    createdAt: toTime(job.createdAt)!,
    completedAt: toTime(job.completedAt),
    progress: job.progress ?? 0,
    items: { noun: 'label', total: job.totalLabels, succeeded: job.successfulLabels, failed: job.failedLabels },
    files: job.files ?? [],
    error: job.error,
  };
}

function fromMappingJob(job: MappingDocumentJobDto): DashboardJob {
  const tags = job.filterTags ?? [];
  const opIds = job.filterOperationIds ?? [];
  const detail = [tags.length ? summarize('Tags', tags) : 'All tags', opIds.length ? summarize('Ops', opIds) : null]
    .filter(Boolean)
    .join(' · ');
  const facts: Array<readonly [string, string]> = [
    ['Tags', tags.length ? tags.join(', ') : 'All'],
    ['Operations', opIds.length ? opIds.join(', ') : 'All'],
  ];
  return {
    id: job.jobId,
    kind: 'mapping',
    status: job.status,
    partnerName: job.partnerName || 'Unknown',
    userId: job.userId,
    typeLabel: 'Mapping document',
    typeDetail: detail,
    facts,
    createdAt: toTime(job.createdAt)!,
    completedAt: toTime(job.completedAt),
    progress: job.progress ?? 0,
    items: null,
    files: job.files ?? [],
    error: job.error,
  };
}

/** Merges the three job APIs into one list, newest first. */
export function toDashboardJobs(
  exportJobs: ExportJobDto[],
  labelJobs: LabelJobDto[],
  mappingJobs: MappingDocumentJobDto[],
): DashboardJob[] {
  return [...exportJobs.map(fromExportJob), ...labelJobs.map(fromLabelJob), ...mappingJobs.map(fromMappingJob)].sort(
    (a, b) => b.createdAt - a.createdAt,
  );
}

const jobKeys = new WeakMap<DashboardJob, string>();
const keyOf = (job: DashboardJob): string => {
  let key = jobKeys.get(job);
  if (key === undefined) {
    key = JSON.stringify(job);
    jobKeys.set(job, key);
  }
  return key;
};

/**
 * Structural sharing for polled data: unchanged jobs keep their previous object identity so
 * memoized rows skip re-rendering, and an unchanged list returns `prev` itself.
 */
export function reconcileJobs(prev: DashboardJob[], next: DashboardJob[]): DashboardJob[] {
  if (prev.length === 0) return next;
  const prevById = new Map(prev.map((job) => [job.id, job]));
  let changed = prev.length !== next.length;
  const merged = next.map((job, i) => {
    const old = prevById.get(job.id);
    const reused = old && keyOf(old) === keyOf(job) ? old : job;
    if (reused !== prev[i]) changed = true;
    return reused;
  });
  return changed ? merged : prev;
}

export const matchesStatus = (job: DashboardJob, filter: StatusFilter): boolean => {
  const statuses = STATUS_FILTER_MATCH[filter];
  return !statuses || statuses.includes(job.status);
};

export interface JobCounts {
  byStatus: Record<StatusFilter, number>;
  byKind: Record<JobKind, number>;
}

export function countJobs(jobs: DashboardJob[]): JobCounts {
  const byStatus: Record<StatusFilter, number> = { all: jobs.length, completed: 0, active: 0, failed: 0 };
  const byKind: Record<JobKind, number> = { export: 0, label: 0, mapping: 0 };
  for (const job of jobs) {
    byKind[job.kind] += 1;
    if (job.status === 'completed') byStatus.completed += 1;
    else if (job.status === 'failed') byStatus.failed += 1;
    else byStatus.active += 1;
  }
  return { byStatus, byKind };
}

/** An empty `kinds` set means no type filter. */
export const filterJobs = (jobs: DashboardJob[], status: StatusFilter, kinds: ReadonlySet<JobKind>): DashboardJob[] =>
  status === 'all' && kinds.size === 0
    ? jobs
    : jobs.filter((job) => matchesStatus(job, status) && (kinds.size === 0 || kinds.has(job.kind)));

export const hasFiles = (job: DashboardJob): boolean => job.status === 'completed' && job.files.length > 0;

export const shortId = (id: string): string => id.slice(0, 8);

export const userHandle = (userId: string): string => userId.split('@')[0];

export const partnerInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z0-9]/.test(w))
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');

export function formatDuration(job: Pick<DashboardJob, 'status' | 'createdAt' | 'completedAt'>, now = Date.now()): string {
  if (job.status === 'pending') return '—';
  const totalSeconds = Math.max(0, Math.floor(((job.completedAt ?? now) - job.createdAt) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
}

const DAY_MS = 86_400_000;

export function formatCreated(createdAt: number, now = Date.now()): { day: string; time: string } {
  const date = new Date(createdAt);
  const startOfToday = new Date(now).setHours(0, 0, 0, 0);
  const time = date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (createdAt >= startOfToday) return { day: 'Today', time };
  if (createdAt >= startOfToday - DAY_MS) return { day: 'Yesterday', time };
  const sameYear = date.getFullYear() === new Date(now).getFullYear();
  const day = date.toLocaleDateString([], { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  return { day, time };
}

export function itemsSummary(items: JobItemCounts): string {
  const plural = (n: number) => `${n} ${items.noun}${n === 1 ? '' : 's'}`;
  if (items.total === 0) return `No ${items.noun}s`;
  return [`${items.succeeded} succeeded`, items.failed ? `${items.failed} failed` : null].filter(Boolean).join(' · ') + ` of ${plural(items.total)}`;
}
